/**
 * BelredenenPanel — de Belredenen-tab op /telefoon.
 *
 * Toont de verdeling van belredenen (effectieve categorie: mens-override boven
 * AI), uitgesplitst naar uitkomst (beantwoord / voicemail / gemist), met trend
 * t.o.v. de vorige even lange periode. Elke categorie draagt de comms-lever als
 * subtekst zodat een piek meteen naar de fix wijst. Onderaan de aanbevelingen
 * uit de laatste wekelijkse AI-analyse.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

interface CategoryStat {
  code: string;
  label: string;
  commsLever: string;
  count: number;
  pct: number;
  deltaPct: number;
}

interface Recommendation {
  categorie?: string;
  aandeel?: string;
  observatie?: string;
  aanbeveling?: string;
  kanaal?: string;
}

interface BelredenenResponse {
  window: { days: number; brand: string | null; from: string; to: string };
  totalWithCategory: number;
  byCategory: CategoryStat[];
  byOutcome: { beantwoord: number; voicemail: number; gemist: number };
  byBrand: Record<string, number>;
  latestInsight: {
    generatedAt: string;
    periodStart: string | null;
    periodEnd: string | null;
    totalCalls: number | null;
    recommendations: Recommendation[];
    model: string | null;
  } | null;
  truncated: boolean;
}

const PERIODS: Array<[number, string]> = [
  [7, '7 dagen'],
  [30, '30 dagen'],
  [90, '90 dagen'],
];

const BRANDS: Array<[string, string]> = [
  ['', 'Alle merken'],
  ['KLEDA', 'Kleda'],
  ['CINCO', 'Cinco'],
  ['SOFASAVER', 'SofaSaver'],
  ['SCHOONHUISSHOP', 'Schoonhuisshop'],
];

function TrendArrow({ delta }: { delta: number }) {
  if (Math.abs(delta) < 0.5) return <span className="text-slate-400 text-xs">–</span>;
  const up = delta > 0;
  return (
    <span className={up ? 'text-amber-600 text-xs' : 'text-emerald-600 text-xs'}>
      {up ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}pp
    </span>
  );
}

export function BelredenenPanel() {
  const [days, setDays] = useState(30);
  const [brand, setBrand] = useState('');
  const [data, setData] = useState<BelredenenResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (d: number, b: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers: Record<string, string> = {};
      if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
      const params = new URLSearchParams({ days: String(d) });
      if (b) params.set('brand', b);
      const r = await fetch(`/api/telephony/belredenen?${params.toString()}`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData(days, brand);
  }, [fetchData, days, brand]);

  const maxCount = data ? Math.max(1, ...data.byCategory.map((c) => c.count)) : 1;
  const recs = data?.latestInsight?.recommendations ?? [];

  return (
    <div className="max-w-3xl space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          {PERIODS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setDays(value)}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                days === value ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <select
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700"
        >
          {BRANDS.map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-800 rounded text-sm">{error}</div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-white border border-slate-200 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : !data || data.totalWithCategory === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-500">
          Nog geen geclassificeerde gesprekken in dit venster. Zodra gesprekken verwerkt zijn, verschijnt de verdeling hier.
        </div>
      ) : (
        <>
          {/* Uitkomst-samenvatting */}
          <div className="grid grid-cols-3 gap-3">
            {([
              ['beantwoord', 'Beantwoord', 'text-emerald-700'],
              ['voicemail', 'Voicemail', 'text-amber-700'],
              ['gemist', 'Gemist', 'text-red-700'],
            ] as const).map(([key, label, color]) => (
              <div key={key} className="bg-white border border-slate-200 rounded-xl p-3 text-center">
                <p className={`text-2xl font-semibold ${color}`}>{data.byOutcome[key]}</p>
                <p className="text-xs text-slate-500 mt-0.5">{label}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 -mt-3">
            {data.totalWithCategory} geclassificeerde gesprekken. Voicemail en gemist zijn het sterkste
            "hadden we kunnen voorkomen"-signaal.
          </p>

          {/* Verdeling per categorie */}
          <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
            {data.byCategory.map((c) => (
              <div key={c.code} className="p-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-slate-800">{c.label}</span>
                  <span className="flex items-center gap-2 text-xs text-slate-500">
                    <TrendArrow delta={c.deltaPct} />
                    <span className="tabular-nums text-slate-700">{c.count} · {c.pct.toFixed(0)}%</span>
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-indigo-500 rounded-full"
                    style={{ width: `${(c.count / maxCount) * 100}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-slate-400">{c.commsLever}</p>
              </div>
            ))}
          </div>

          {/* Wekelijkse aanbevelingen */}
          <div>
            <h2 className="text-sm font-semibold text-slate-700 mb-2">Aanbevelingen (wekelijkse analyse)</h2>
            {recs.length === 0 ? (
              <p className="text-sm text-slate-500">
                Nog geen analyse beschikbaar. De wekelijkse AI-analyse draait elke maandagochtend en
                vult hier concrete comms-aanbevelingen in.
              </p>
            ) : (
              <div className="space-y-3">
                {data.latestInsight?.generatedAt && (
                  <p className="text-xs text-slate-400">
                    Laatste analyse: {new Date(data.latestInsight.generatedAt).toLocaleDateString('nl-NL', {
                      day: '2-digit', month: '2-digit', year: 'numeric',
                    })}
                  </p>
                )}
                {recs.map((rec, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {rec.categorie && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">{rec.categorie}</span>
                      )}
                      {rec.aandeel && (
                        <span className="text-xs text-slate-500">{rec.aandeel}</span>
                      )}
                      {rec.kanaal && (
                        <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ml-auto">
                          {rec.kanaal}
                        </span>
                      )}
                    </div>
                    {rec.observatie && <p className="text-sm text-slate-600">{rec.observatie}</p>}
                    {rec.aanbeveling && <p className="text-sm text-slate-900 font-medium mt-1">{rec.aanbeveling}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
